import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ArrowLeft, Download, Copy, CheckCircle, Clock, XCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Badge } from "../../../shadcn/ui/badge";
import { Constants } from "../../../utils/constants";
import toastUtils from "../../../utils/toast";
import { showApiMessage } from "../../../utils/messageHandler";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import logger from "../../../utils/logger";


const ViewInvoice = () => {
  const history = useHistory();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const previewRef = React.useRef();
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const userCurrencyCode = useUserCurrency();

  // Check if user is HOST_PROPERTY_MANAGEMENT
  const currentUser = localService.getUser();
  const isHostPropertyManagement = currentUser?.organization?.type?.code === "HOST_PROPERTY_MANAGEMENT";

  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!isHostPropertyManagement) {
      toastUtils.error("Access Denied", {
        description: "Only property management users can view invoices.",
      });
      history.push("/host/invoice");
    }
  }, [isHostPropertyManagement, history]);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError(null);

        logger.log("Fetching invoice with ID:", id);
        const response = await billingService.getInvoiceById(parseInt(id));

        logger.log("Invoice API Response:", response);

        if (response.status === 200) {
          setInvoice(response.data?.data || response.data);
        } else {
          setError("Failed to load invoice");
          showApiMessage(response, "load invoice");
        }
      } catch (err) {
        logger.error("Error fetching invoice:", err);
        setError("Failed to load invoice");
        showApiMessage(err, "load invoice");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoice();
    }
  }, [id]);

  // Fetch transactions related to this invoice
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!invoice?.id) return;
      try {
        setTxLoading(true);
        const resp = await billingService.getTransactions({
          invoiceId: invoice.id,
          page: 1,
          limit: 50,
          orderBy: "createdAt:desc",
        });
        if (resp?.status === 200) {
          const rows = resp.data?.data || resp.data || [];
          setTransactions(rows);
        } else {
          setTransactions([]);
        }
      } catch (e) {
        logger.error("Error fetching transactions for invoice:", e);
        setTransactions([]);
      } finally {
        setTxLoading(false);
      }
    };

    fetchTransactions();
  }, [invoice]);

  const formatCurrency = amount => {
    const currency = userCurrencyCode || "KES";
    return `${currency} ${Number(amount || 0).toLocaleString()}`;
  };

  const handleBack = () => {
    history.push("/host/invoice");
  };

  const handleDownloadPDF = async () => {
    try {
      if (!previewRef.current) return;

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice-${invoice?.referenceCode || invoice?.id || "unknown"}.pdf`);
      toastUtils.success("PDF downloaded successfully!");
    } catch (error) {
      logger.error("Error generating PDF:", error);
      toastUtils.error("Failed to download PDF", {
        description: "Please try again later.",
      });
    }
  };

  const handleCopyInvoice = () => {
    // Copy invoice details to clipboard
    const currency = userCurrencyCode || "KES";
    const invoiceText = `Invoice ${invoice?.referenceCode || invoice?.id}
Amount: ${currency} ${parseFloat(invoice?.amount || 0).toFixed(2)}
Status: ${invoice?.status}
Date: ${new Date(invoice?.createdAt).toLocaleDateString()}`;

    navigator.clipboard
      .writeText(invoiceText)
      .then(() => {
        toastUtils.success("Invoice details copied to clipboard!");
      })
      .catch(() => {
        toastUtils.error("Failed to copy to clipboard");
      });
  };

  const getStatusIcon = status => {
    switch (status) {
      case "PAID":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "PENDING":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "CANCELLED":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case "PAID":
        return "text-green-600 bg-green-100";
      case "PENDING":
        return "text-yellow-600 bg-yellow-100";
      case "CANCELLED":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="space-y-4 animate-pulse">
          <div className="w-64 h-8 rounded bg-slate-200"></div>
          <div className="w-full h-96 rounded bg-slate-200"></div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Invoice Not Found</h2>
          <p className="mb-6 text-gray-600">The invoice you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  // Transform API data to match the preview format
  const displayData = {
    companyName: "Spacia Workspace Solutions",
    companyEmail: "billing@spacia.page",
    companyPhone: "+1 (555) 123-4567",
    invoiceNumber: invoice.referenceCode || `INV-${invoice.id}`,
    invoiceDate: new Date(invoice.createdAt).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    clientName: "Client",
    clientAddress: "Client Address",
    clientCity: "Client City",
    clientVAT: "VAT123456789",
    clientEmail: "client@email.com",
    clientPhone: "+1 (555) 987-6543",
    bankDetails: "Spacia Bank",
    iban: "US12-3456-7890-1234",
    discount: 0,
    notes: "Payment due within 30 days. Late payments may incur additional charges.",
  };

  // Create invoice items from API data
  const displayItems = [
    {
      id: 1,
      description: invoice.description || "Invoice Item",
      rate: parseFloat(invoice.amount || 0),
      hours: 1,
      amount: parseFloat(invoice.amount || 0),
    },
  ];

  const displaySubtotal = displayItems.reduce((sum, item) => sum + item.amount, 0);
  const displayDiscountAmount = (displaySubtotal * displayData.discount) / 100;
  const displayTotal = displaySubtotal - displayDiscountAmount;

  return (
    <div className="p-4 min-h-screen bg-gray-100">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button onClick={handleBack} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Invoice Details</h1>
                <p className="text-sm text-gray-500">{invoice.referenceCode || `Invoice #${invoice.id}`}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(invoice.status)}`}
              >
                {getStatusIcon(invoice.status)}
                <span>{invoice.status}</span>
              </div>

              <button
                onClick={handleCopyInvoice}
                className="p-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                title="Copy invoice details"
              >
                <Copy className="w-4 h-4" />
              </button>

              <button
                onClick={handleDownloadPDF}
                className="p-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Preview - Exact match to AddInvoice preview */}
        <div className="overflow-y-auto p-3 w-full max-h-screen bg-white rounded-2xl shadow-xs">
          <div className="">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-bold text-gray-900">Invoice</h2>
              <div className="flex space-x-1">
                <button
                  onClick={handleDownloadPDF}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                  title="Download PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleCopyInvoice}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                  title="Copy invoice details"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Invoice Preview */}
            <div ref={previewRef} className="p-6 bg-white">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-2">
                  <div
                    className="flex justify-center items-center w-8 h-8 rounded-lg"
                    style={{
                      backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                    }}
                  >
                    <span className="text-sm font-bold text-white">{displayData.companyName.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{displayData.companyName}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 mb-0.5">
                    <span className="inline-block mr-1 w-1 h-1 bg-gray-400 rounded-full"></span>
                    {displayData.companyEmail}
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="inline-block mr-1 w-1 h-1 bg-gray-400 rounded-full"></span>
                    {displayData.companyPhone}
                  </div>
                </div>
              </div>

              {/* Invoice Title and Details */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="mb-1 text-xs text-gray-500">RECIPIENT</div>
                  <div className="text-xs font-semibold text-gray-900">{displayData.clientName}</div>
                  <div className="text-xs text-gray-500">{displayData.clientAddress}</div>
                  <div className="text-xs text-gray-500">{displayData.clientCity}</div>
                  <div className="text-xs text-gray-500">{displayData.clientVAT}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="inline-block mr-1 w-1 h-1 bg-gray-400 rounded-full"></span>
                    {displayData.clientEmail}
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="inline-block mr-1 w-1 h-1 bg-gray-400 rounded-full"></span>
                    {displayData.clientPhone}
                  </div>
                </div>
                <div className="text-right">
                  <h1 className="mb-2 text-lg font-bold text-gray-900">Invoice</h1>
                  <div className="text-xs text-gray-500">Invoice no.</div>
                  <div className="text-xs font-semibold">{displayData.invoiceNumber}</div>
                  <div className="mt-1 text-xs text-gray-500">Invoice date</div>
                  <div className="text-xs font-semibold">{displayData.invoiceDate}</div>
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-4">
                <div className="grid grid-cols-12 gap-2 py-1 text-xs font-medium text-gray-500 uppercase border-b border-gray-200">
                  <div className="col-span-5">Task Description</div>
                  <div className="col-span-2 text-center">Hours</div>
                  <div className="col-span-2 text-center">Rate</div>
                  <div className="col-span-3 text-right">Amount</div>
                </div>

                {displayItems.map(item => (
                  <div key={item.id} className="grid grid-cols-12 gap-2 py-1 border-b border-gray-100">
                    <div className="col-span-5 text-xs text-gray-900">{item.description || "Invoice Item"}</div>
                    <div className="col-span-2 text-xs text-center text-gray-600">{item.hours}</div>
                    <div className="col-span-2 text-xs text-center text-gray-600">
                      {userCurrencyCode || "KES"} {item.rate.toFixed(2)}
                    </div>
                    <div className="col-span-3 text-xs text-right text-gray-900">
                      {userCurrencyCode || "KES"} {item.amount.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="flex justify-end mb-4">
                <div className="w-40">
                  <div className="flex justify-between py-0.5">
                    <span className="text-xs text-gray-500">SUBTOTAL</span>
                    <span className="text-xs text-gray-900">
                      {userCurrencyCode || "KES"} {displaySubtotal.toFixed(2)}
                    </span>
                  </div>
                  {displayData.discount > 0 && (
                    <div className="flex justify-between py-0.5">
                      <span className="text-xs text-gray-500">DISCOUNT {displayData.discount}%</span>
                      <span className="text-xs text-gray-900">
                        {userCurrencyCode || "KES"} {displayDiscountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-0.5 border-t border-gray-200 font-semibold">
                    <span className="text-xs text-gray-900">TOTAL</span>
                    <span className="text-sm" style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}>
                      {userCurrencyCode || "KES"} {displayTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="mb-3 text-xs text-center text-gray-500">
                to the business account below. Please include invoice number on your check.
              </div>

              <div className="flex justify-center mb-4 space-x-4 text-xs text-gray-500">
                <div>
                  <span className="font-medium">BANK:</span> {displayData.bankDetails}
                </div>
                <div>
                  <span className="font-medium">IBAN:</span> {displayData.iban}
                </div>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <div className="mb-1 text-xs font-medium text-gray-900">NOTES</div>
                <div className="text-xs leading-relaxed text-gray-500 whitespace-pre-line">{displayData.notes}</div>
              </div>

              {/* Related Transactions */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Related Transactions</h3>
                {txLoading ? (
                  <div className="text-sm text-gray-500">Loading transactions...</div>
                ) : transactions.length === 0 ? (
                  <div className="text-sm text-gray-500">No transactions found for this invoice.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Reference</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Description</TableHead>
                          <TableHead className="font-semibold">Amount</TableHead>
                          <TableHead className="font-semibold">Type</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map(tx => (
                          <TableRow key={tx.id} className="hover:bg-muted/50">
                            <TableCell>
                              <div className="text-sm font-mono text-primary font-medium">
                                {tx.reference || tx.invoiceReference || `TX-${tx.id}`}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "-"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {tx.description || "-"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-semibold">
                                {formatCurrency(parseFloat(tx.amount || tx.value || 0))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {tx.type === "CREDIT" || tx.type === "credit" || tx.type === true
                                  ? "Payment"
                                  : tx.type === "DEBIT" || tx.type === "debit" || tx.type === false
                                    ? "Invoice"
                                    : tx.transactionType || "—"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="default" className="text-xs">
                                {tx.status || "—"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="pt-4 mt-6 border-t border-gray-200">
                <div className="flex justify-between items-start text-xs text-gray-500">
                  <div className="flex-1">
                    <div className="mb-1 font-medium text-gray-700">{displayData.companyName}</div>
                    <div>1331 Hart Ridge Road, 48436 Gaines, MI</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="flex justify-center items-center mb-1 space-x-1">
                      <span className="inline-block w-1 h-1 bg-blue-500 rounded-full"></span>
                      <span>{displayData.companyEmail}</span>
                    </div>
                    <div className="flex justify-center items-center space-x-1">
                      <span className="inline-block w-1 h-1 bg-blue-500 rounded-full"></span>
                      <span>{displayData.companyPhone}</span>
                    </div>
                  </div>
                  <div className="flex-1 text-right">
                    <div>The company is registered in the</div>
                    <div>business register under no. 87650000</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewInvoice;
